import * as stylex from "@stylexjs/stylex";
import { colors, radii } from "@softmax/design-system/tokens.stylex";
import { type ReactNode } from "react";

import type { CatalogMediaItem } from "./media-types";
import { styles as mediaStyles } from "./media.styles";

export function MediaThumbnail({
  item,
  visual,
  selected,
  onSelect,
}: {
  readonly item: CatalogMediaItem;
  readonly visual: CatalogMediaItem | undefined;
  readonly selected: boolean;
  readonly onSelect: () => void;
}): ReactNode {
  const hasPlayGlyph =
    item.kind === "board" ||
    item.kind === "highlight" ||
    item.kind === "replay";
  return (
    <button
      type="button"
      data-store-thumb={item.kind}
      aria-pressed={selected}
      aria-label={`Show ${item.alt}`}
      onClick={onSelect}
      {...stylex.props(styles.thumbnail)}
    >
      {visual && visual.kind !== "board" ? (
        <img
          src={visual.poster ?? visual.src}
          alt=""
          {...stylex.props(mediaStyles.image)}
        />
      ) : null}
      {hasPlayGlyph ? (
        <span aria-hidden="true" {...stylex.props(styles.play)}>
          <span {...stylex.props(styles.playDisc)}>
            <svg
              width="10"
              height="10"
              viewBox="0 0 16 16"
              fill="currentColor"
              aria-hidden="true"
            >
              <path d="M4 2.5v11l10-5.5Z" />
            </svg>
          </span>
        </span>
      ) : null}
      {selected ? (
        <span aria-hidden="true" {...stylex.props(styles.selectedRing)} />
      ) : null}
    </button>
  );
}

const styles = stylex.create({
  thumbnail: {
    position: "relative",
    aspectRatio: "16 / 9",
    width: 96,
    flexShrink: 0,
    padding: 0,
    overflow: "clip",
    borderRadius: radii.small,
    borderWidth: 1,
    borderStyle: "solid",
    borderColor: colors.border,
    backgroundColor: colors.surfaceAlt,
    cursor: "pointer",
    outline: {
      default: "none",
      ":focus-visible": `2px solid ${colors.foreground}`,
    },
    outlineOffset: 2,
  },
  selectedRing: {
    position: "absolute",
    inset: 0,
    borderRadius: radii.small,
    boxShadow: `inset 0 0 0 2px ${colors.borderStrong}`,
  },
  play: {
    position: "absolute",
    inset: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  playDisc: {
    display: "flex",
    width: 22,
    height: 22,
    alignItems: "center",
    justifyContent: "center",
    borderRadius: radii.pill,
    borderWidth: 1,
    borderStyle: "solid",
    borderColor: colors.border,
    backgroundColor: colors.background,
    color: colors.foreground,
  },
});
